import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchLikeState, likeArticle, unlikeArticle } from '../services/newsLikes';
import { useUserStore } from '../store/userStore';

/**
 * Likes du fil News.
 *
 * MÊME TRAITEMENT OPTIMISTE QUE LE FAVORI, pour la même raison : un cœur qui attend un aller-retour
 * réseau pour se remplir donne l'impression que l'appui n'a pas porté, et provoque un second appui —
 * donc un retrait immédiat de ce qu'on venait de poser. Le compteur suit la bascule, lui aussi
 * localement, sinon il dirait le contraire du cœur pendant une seconde.
 *
 * NON CONNECTÉ : rien n'est chargé et rien n'est affiché (le cœur est masqué, comme le signet). On
 * n'interroge pas Supabase pour se voir refuser par la RLS.
 */
export function useNewsLikes(articleIds: string[]) {
  const session = useUserStore((state) => state.session);
  const connecte = !!session;

  const [counts, setCounts] = useState<Record<string, number>>({});
  const [mine, setMine] = useState<Set<string>>(new Set());
  const [erreur, setErreur] = useState(false);

  /**
   * Ce qui a DÉJÀ été demandé. Les identifiants arrivent par paquets à chaque page, et sans cette
   * mémoire on redemanderait l'état de tout le fil à chaque nouvelle page — dix requêtes pour dix
   * pages, sur des données qu'on a déjà.
   */
  const demandes = useRef<Set<string>>(new Set());
  const monte = useRef(true);

  useEffect(() => {
    monte.current = true;
    return () => {
      monte.current = false;
    };
  }, []);

  // La déconnexion vide tout : garder un cœur rempli après une déconnexion afficherait l'état
  // de quelqu'un d'autre.
  useEffect(() => {
    if (!connecte) {
      demandes.current = new Set();
      setCounts({});
      setMine(new Set());
    }
  }, [connecte]);

  useEffect(() => {
    if (!connecte) return;
    const nouveaux = articleIds.filter((id) => !demandes.current.has(id));
    if (nouveaux.length === 0) return;
    nouveaux.forEach((id) => demandes.current.add(id));

    void (async () => {
      try {
        const etat = await fetchLikeState(nouveaux);
        if (!monte.current) return;
        setCounts((actuels) => ({ ...actuels, ...etat.counts }));
        setMine((actuels) => new Set([...actuels, ...etat.mine]));
      } catch {
        // On réautorise une nouvelle tentative au prochain paquet plutôt que de figer un état
        // partiel : sans cela, un creux de réseau laisserait ces articles sans compteur pour
        // toute la session.
        nouveaux.forEach((id) => demandes.current.delete(id));
      }
    })();
  }, [articleIds, connecte]);

  const basculer = useCallback(
    async (articleId: string) => {
      if (!connecte) return;
      const etaitLike = mine.has(articleId);
      const delta = etaitLike ? -1 : 1;

      // ── Bascule immédiate du cœur ET du compteur, avant tout appel réseau ──
      setMine((actuels) => {
        const suivant = new Set(actuels);
        if (etaitLike) suivant.delete(articleId);
        else suivant.add(articleId);
        return suivant;
      });
      setCounts((actuels) => ({
        // `Math.max(…, 0)` en écho au `greatest` du trigger : l'affichage ne doit pas pouvoir
        // montrer un nombre négatif, même si le compteur local part d'une valeur en retard.
        ...actuels,
        [articleId]: Math.max((actuels[articleId] ?? 0) + delta, 0),
      }));

      try {
        if (etaitLike) await unlikeArticle(articleId);
        else await likeArticle(articleId);
      } catch {
        if (!monte.current) return;
        // ── ROLLBACK exact : le cœur et le compteur reviennent ensemble ──
        setMine((actuels) => {
          const suivant = new Set(actuels);
          if (etaitLike) suivant.add(articleId);
          else suivant.delete(articleId);
          return suivant;
        });
        setCounts((actuels) => ({
          ...actuels,
          [articleId]: Math.max((actuels[articleId] ?? 0) - delta, 0),
        }));
        setErreur(true);
      }
    },
    [connecte, mine],
  );

  return {
    connecte,
    counts,
    mine,
    erreur,
    effacerErreur: useCallback(() => setErreur(false), []),
    basculer,
  };
}
